import { TARGET_YEAR } from '../../common/data';
import { getAlertMessageFunction } from '../../common/Util';

/**
 * 필드별 최대 입력 길이
 */
export const FIELD_MAX_LENGTH = {
  htm003: 10, // 사업자번호
};

/**
 * 근무월수 기반 한도 체크 필드 (월 20만원 한도)
 */
export const MONTHLY_LIMIT_FIELDS_20 = {
  htm045: '유아·초중등',
  htm046: '고등교육법',
  htm047: '특별법',
  htm048: '나-연구보조비',
  htm049: '다-연구보조비',
  htm075: '취재수당',
  htm076: '벽지수당',
  htm086: '이주수당',
  htm093: '비과세식사대',
  htm022: '보육수당',
};

/**
 * 근무월수 기반 한도 체크 필드 (월 100만원 한도)
 */
export const MONTHLY_LIMIT_FIELDS_100 = {
  htm057: '국외근로(100만원)',
};

/**
 * 근무월수 기반 한도 체크 필드 (월 500만원 한도)
 */
export const MONTHLY_LIMIT_FIELDS_500 = {
  htm058: '국외근로(500만원)',
};

/**
 * 입력값 처리 (trim + 길이 제한)
 * @param {string} key - 필드 키
 * @param {string} value - 입력값
 * @returns {string} 처리된 값
 */
export const processInputValue = (key, value) => {
  let processedValue = value.trim();

  // 근무처명(htm002)을 제외한 필드는 숫자만 입력 허용
  if (key !== 'htm002') {
    processedValue = processedValue.replace(/[^0-9]/g, '');
  }

  // 최대 길이 제한
  const maxLength = FIELD_MAX_LENGTH[key];
  if (maxLength && processedValue.length > maxLength) {
    processedValue = processedValue.slice(0, maxLength);
  }

  return processedValue;
};

/**
 * 날짜 문자열 유효성 검사
 * @param {string} dateStr - YYYYMMDD 형식의 날짜 문자열
 * @returns {boolean} 유효한 날짜인지 여부
 */
export const isValidDate = dateStr => {
  if (!dateStr || dateStr.length !== 8) return false;
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6));
  const day = parseInt(dateStr.substring(6, 8));

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

/**
 * 전직장/납세조합 폼 유효성 검사
 * @param {Object} formData - 폼 데이터
 * @param {string} htmJType - 타입 ('1': 전직장, '2': 납세조합)
 * @param {Array} headers - 헤더 목록
 * @returns {boolean} 유효한 폼인지 여부
 */
export const isValidate = (formData, htmJType, headers, t) => {
  const alertMessage = getAlertMessageFunction();
  const htmYy = TARGET_YEAR;

  // 값 가져오기 헬퍼 (trim 처리)
  const getValue = key => {
    const value = formData[key];
    if (value === undefined || value === null) return '';
    return String(value).trim();
  };

  // 필수값 체크 키 목록
  const requiredKeys =
    htmJType === '1'
      ? [
          'htm002',
          'htm003',
          'htm019',
          'htm020',
          'htm005',
          'htm014',
          'htm012',
          'htm013',
          'htm015',
          'htm016',
        ]
      : ['htm002', 'htm003', 'htm019', 'htm020'];

  // 필수값 체크
  const emptyKey = requiredKeys.find(key => getValue(key) === '');

  if (emptyKey) {
    alertMessage(t('validation.requiredFields'));
    return false;
  }

  // 사업자번호 검증 (10자리)
  const htm003 = getValue('htm003').replace(/-/g, '');
  if (htm003.length < 10) {
    alertMessage(t('validation.invalidBusinessNumber'));
    return false;
  }

  // 날짜값 정리
  const htm019 = getValue('htm019').replace(/-/g, '');
  const htm020 = getValue('htm020').replace(/-/g, '');
  const htm079 = getValue('htm079').replace(/-/g, '');
  const htm080 = getValue('htm080').replace(/-/g, '');

  // 근무기간 연도 체크 (당해년도만 가능)
  if (htm019.substring(0, 4) !== String(htmYy) || htm020.substring(0, 4) !== String(htmYy)) {
    alertMessage(t('validation.workPeriodYearOnly'));
    return false;
  }

  // 감면기간 연도 체크
  if (htm079 && htm080) {
    if (htm079.substring(0, 4) !== String(htmYy) || htm080.substring(0, 4) !== String(htmYy)) {
      alertMessage(t('validation.reductionPeriodYearOnly'));
      return false;
    }
  }

  // 근무기간 날짜 유효성
  if (!isValidDate(htm019) || !isValidDate(htm020)) {
    alertMessage(t('validation.invalidWorkPeriod'));
    return false;
  }

  // 감면기간 날짜 유효성
  if (htm079 && htm080 && (!isValidDate(htm079) || !isValidDate(htm080))) {
    alertMessage(t('validation.invalidReductionPeriod'));
    return false;
  }

  // 근무기간 순서 (시작 <= 종료)
  if (htm019 > htm020) {
    alertMessage(t('validation.workEndDateMustBeAfterStart'));
    return false;
  }

  // 감면기간 순서
  if (htm079 && htm080 && htm079 > htm080) {
    alertMessage(t('validation.reductionEndDateMustBeAfterStart'));
    return false;
  }

  // 감면기간이 근무기간 내에 있어야 함
  if (htm079 && htm079 < htm019) {
    alertMessage(t('validation.reductionStartMustBeAfterWorkStart'));
    return false;
  }

  if (htm080 && htm080 > htm020) {
    alertMessage(t('validation.reductionEndMustBeBeforeWorkEnd'));
    return false;
  }

  // 감면액 섹션 체크
  const gammyeonKeys = headers
    .filter(item => item.title?.includes('감면액') && item.key)
    .map(item => item.key);

  const hasGammyeon = gammyeonKeys.some(key => {
    const value = getValue(key);
    return value !== '' && value !== '0';
  });

  if (hasGammyeon && (!htm079 || !htm080)) {
    alertMessage(t('validation.reductionPeriodRequired'));
    return false;
  }

  // 급여 합계 vs 비과세 합계 체크
  const kupYoKeys = headers
    .filter(item => (item.title?.includes('급여') || item.key === 'htm005') && item.key)
    .map(item => item.key);

  const taxExemptKeys = headers
    .filter(item => item.title?.includes('비과세') && item.key)
    .map(item => item.key);

  const kupYoSum = kupYoKeys.reduce((sum, key) => {
    const value = parseInt(getValue(key).replace(/,/g, '') || '0', 10) || 0;
    return sum + value;
  }, 0);

  const taxExemptSum = taxExemptKeys.reduce((sum, key) => {
    const value = parseInt(getValue(key).replace(/,/g, '') || '0', 10) || 0;
    return sum + value;
  }, 0);

  if (kupYoSum === 0 && taxExemptSum !== 0) {
    alertMessage(t('validation.invalidIncomeTaxExemptSum'));
    return false;
  }

  // 근무월수 기반 한도 체크
  // MIN(근무월수 + 1, 12) × 한도금액
  const workingMonths = Math.min(
    parseInt(htm020.substring(4, 6)) - parseInt(htm019.substring(4, 6)) + 2,
    12,
  );

  // 월 20만원 한도 필드 체크
  for (const [key, name] of Object.entries(MONTHLY_LIMIT_FIELDS_20)) {
    const value = getValue(key);
    const limit = workingMonths * 200000;
    if (value && parseInt(value, 10) > limit) {
      alertMessage(t('validation.monthlyLimit20', { name }));
      return false;
    }
  }

  // 월 100만원 한도 필드 체크
  for (const [key, name] of Object.entries(MONTHLY_LIMIT_FIELDS_100)) {
    const value = getValue(key);
    const limit = workingMonths * 1000000;
    if (value && parseInt(value, 10) > limit) {
      alertMessage(t('validation.monthlyLimit100', { name }));
      return false;
    }
  }

  // 월 500만원 한도 필드 체크
  for (const [key, name] of Object.entries(MONTHLY_LIMIT_FIELDS_500)) {
    const value = getValue(key);
    const limit = workingMonths * 5000000;
    if (value && parseInt(value, 10) > limit) {
      alertMessage(t('validation.monthlyLimit500', { name }));
      return false;
    }
  }

  // 주식매수선택권 한도 (3000만원)
  const htm061 = getValue('htm061');
  if (htm061 && parseInt(htm061, 10) > 30000000) {
    alertMessage(t('validation.stockOptionLimit'));
    return false;
  }

  // 벤처기업 주식매수선택권 한도 (2억원)
  const htm092 = getValue('htm092');
  if (htm092 && parseInt(htm092, 10) > 200000000) {
    alertMessage(t('validation.ventureStockOptionLimit'));
    return false;
  }

  return true;
};

/**
 * 다양한 날짜 형식을 YYYY-MM-DD 형식으로 변환
 * @param {string} dateStr - 날짜 문자열 (YYYYMMDD, YYYY.MM.DD, YYYY/MM/DD 등)
 * @returns {string} YYYY-MM-DD 형식의 날짜 문자열
 */
const formatDateWithDash = dateStr => {
  if (!dateStr) return dateStr;

  // 구분자(., /, -)를 모두 제거하여 YYYYMMDD 형식으로 변환
  const cleanedDate = dateStr.replace(/[.\-/]/g, '');

  if (cleanedDate.length !== 8) return dateStr;

  return `${cleanedDate.substring(0, 4)}-${cleanedDate.substring(4, 6)}-${cleanedDate.substring(6, 8)}`;
};

/**
 * AI 파싱 데이터 처리 함수
 * - key가 있는 값만 사용
 * - 사업자등록번호(htm003) 제외하고 value에 '-'가 있으면 빈값 처리
 * - 날짜 필드(htm019, htm020, htm079, htm080)는 YYYY-MM-DD 형식으로 변환
 * @param {Object} aiData - API 응답 데이터
 * @param {Array} headers - 헤더 리스트 (key, endKey 포함)
 * @returns {Object} 처리된 폼 데이터
 */
export const parseAIData = (aiData, headers) => {
  if (!aiData) return null;

  const validKeys = headers.map(item => item.key).filter(Boolean);
  // endKey도 포함 (htm020, htm080 등)
  const endKeys = headers.map(item => item.endKey).filter(Boolean);
  const allValidKeys = [...validKeys, ...endKeys];

  // 날짜 필드 목록
  const dateKeys = ['htm019', 'htm020', 'htm079', 'htm080'];

  // isNumber가 true인 필드 목록
  const numberKeys = headers.filter(item => item.isNumber).map(item => item.key);

  const parsedData = {};

  for (const key of allValidKeys) {
    if (aiData[key] !== undefined && aiData[key] !== null) {
      let value = String(aiData[key]);

      // 날짜 필드는 YYYY-MM-DD 형식으로 변환
      if (dateKeys.includes(key)) {
        value = formatDateWithDash(value);
      } else if (key !== 'htm003' && value.includes('-')) {
        // 사업자등록번호(htm003)와 날짜 필드 제외하고 '-'가 포함되면 빈값 처리
        value = '';
      }

      // isNumber가 true인 필드는 콤마 제거
      if (numberKeys.includes(key)) {
        value = value.replace(/,/g, '');
      }

      parsedData[key] = value;
    }
  }

  return parsedData;
};
